/**
 * Mock data for the Accounting Overview page.
 *
 * This file is the ONLY place that fakes server data for Overview.
 * When the backend is wired up, each MOCK_* export is replaced by
 * an API fetch. No UI component should be touched.
 *
 * Figures below reflect the "populated" dashboard state. The
 * "empty state" scene is triggered by passing an empty accounts
 * array to the Overview page, not by changing this file.
 */

import {
  Eye,
  Scale,
  BookOpen,
  CreditCard,
  FileText,
  Banknote,
  ReceiptText,
  ArrowRightLeft,
  Coins,
  CircleDollarSign,
} from "lucide-react";
import type {
  OverviewStat,
  QuickAction,
  AccountSummary,
  RecentActivityItem,
  ReconciliationHealthRow,
  VelaSummary,
  CashFlowPoint,
  SectionStatus,
} from "./overviewTypes";

/** Current period stats (defaults to This month). */
export const MOCK_OVERVIEW_STATS: OverviewStat[] = [
  { id: "cash_on_hand", label: "Cash on hand", value: 54952.25, currency: "CAD", delta: 2140.80, deltaDirection: "up", deltaLabel: "vs last month" },
  { id: "income_mtd", label: "Income this month", value: 18420.00, currency: "CAD", delta: 1250.00, deltaDirection: "up", deltaLabel: "vs last month" },
  { id: "expenses_mtd", label: "Expenses this month", value: 11084.75, currency: "CAD", delta: 320.15, deltaDirection: "down", deltaLabel: "vs last month" },
  { id: "net_profit", label: "Net profit", value: 7335.25, currency: "CAD", delta: 1570.15, deltaDirection: "up", deltaLabel: "vs last month" },
];

/**
 * Quick action circles in the sage Create new panel.
 *
 * 9 actions, grid of 6 columns: first 6 fill row 1, next 3 left-aligned
 * on row 2 (matches the standing Novala Quick Actions pattern).
 * Every route is real. Nothing dead.
 */
export const MOCK_QUICK_ACTIONS: QuickAction[] = [
  { id: "transaction", icon: CreditCard,       title: "Transaction",       description: "Record a bank transaction",  tooltip: "New bank transaction",          route: "/accounting/bank-transactions?new=1" },
  { id: "invoice",     icon: FileText,         title: "Invoice",           description: "Send an invoice to a customer", tooltip: "Send a new invoice",         route: "/invoices/new" },
  { id: "bill",        icon: Banknote,         title: "Bill",              description: "Record a vendor bill",       tooltip: "Record a vendor bill",          route: "/billpay" },
  { id: "receipt",     icon: ReceiptText,      title: "Receipt",           description: "Snap or upload a receipt",   tooltip: "Snap or upload a receipt",      route: "/accounting/receipts?new=1" },
  { id: "transfer",    icon: ArrowRightLeft,   title: "Transfer",          description: "Transfer between accounts",  tooltip: "Transfer between accounts",     route: "/accounting/bank-transactions?new=transfer" },
  { id: "payment",     icon: CircleDollarSign, title: "Payment",           description: "Receive a customer payment", tooltip: "Receive a customer payment",    route: "/invoices" },
  { id: "reconcile",   icon: Scale,            title: "Reconcile",         description: "Reconcile an account",       tooltip: "Start a reconciliation",        route: "/accounting/reconcile" },
  { id: "journal",     icon: Coins,            title: "Journal entry",     description: "Post a manual journal entry", tooltip: "Post a manual journal entry",  route: "/ledger" },
  { id: "chart",       icon: BookOpen,         title: "Chart of accounts", description: "Open the chart of accounts", tooltip: "Open the chart of accounts",    route: "/accounting/chart-of-accounts" },
  // Legacy entries kept for anywhere else that may still reference them (safe to drop later).
  { id: "review",      icon: Eye,              title: "Review new transactions", description: "14 transactions need your eyes", route: "/accounting/bank-transactions", badgeCount: 14 },
];

/** Connected accounts summary (4 accounts across 2 banks + petty cash). */
export const MOCK_ACCOUNTS: AccountSummary[] = [
  { id: "acc_rbc_chq_4821", bankBrand: "rbc", accountName: "RBC Chequing",     accountNumberLast4: "4821", balance: 10214.80, currency: "CAD", isConnected: true,  lastSyncedAt: "2026-10-24T06:00:00Z" },
  { id: "acc_rbc_sav_9112", bankBrand: "rbc", accountName: "RBC Savings",      accountNumberLast4: "9112", balance: 42890.12, currency: "CAD", isConnected: true,  lastSyncedAt: "2026-10-24T06:00:00Z" },
  { id: "acc_td_visa_0028", bankBrand: "td",  accountName: "TD Visa Business", accountNumberLast4: "0028", balance: 1847.33,  currency: "CAD", isConnected: true,  lastSyncedAt: "2026-10-24T06:00:00Z" },
  { id: "acc_cash",         bankBrand: "other", accountName: "Petty cash",     accountNumberLast4: "----", balance: 125.00,   currency: "CAD", isConnected: false },
];

/** Recent activity (last 5 transactions across all accounts). */
export const MOCK_RECENT_ACTIVITY: RecentActivityItem[] = [
  { id: "txn_001", date: "2026-10-19", payee: "Oak Valley Property Mgmt", amount: 4200.00, currency: "CAD", direction: "in",  accountId: "acc_rbc_chq_4821", category: "Rental income" },
  { id: "txn_002", date: "2026-10-18", payee: "Costco Wholesale #482",    amount: 171.30,  currency: "CAD", direction: "out", accountId: "acc_td_visa_0028", category: "Office supplies" },
  { id: "txn_003", date: "2026-10-17", payee: "Payroll run (biweekly)",   amount: 3842.00, currency: "CAD", direction: "out", accountId: "acc_rbc_chq_4821", category: "Payroll" },
  { id: "txn_004", date: "2026-10-16", payee: "Stripe payout",            amount: 1485.90, currency: "CAD", direction: "in",  accountId: "acc_rbc_chq_4821", category: "Sales" },
  { id: "txn_005", date: "2026-10-15", payee: "Shaw Business Internet",   amount: 149.95,  currency: "CAD", direction: "out", accountId: "acc_td_visa_0028", category: "Utilities" },
];

/** Reconciliation health across all accounts. */
export const MOCK_RECONCILIATION_HEALTH: ReconciliationHealthRow[] = [
  { id: "rec_001", accountId: "acc_rbc_chq_4821", accountName: "RBC Chequing",     bankBrand: "rbc",   lastReconciledDate: "2026-09-30", lastReconciledBalance: 8921.42,  currency: "CAD", status: "due_soon" },
  { id: "rec_002", accountId: "acc_rbc_sav_9112", accountName: "RBC Savings",      bankBrand: "rbc",   lastReconciledDate: "2026-09-30", lastReconciledBalance: 42500.00, currency: "CAD", status: "up_to_date" },
  { id: "rec_003", accountId: "acc_td_visa_0028", accountName: "TD Visa Business", bankBrand: "td",    lastReconciledDate: "2026-08-31", lastReconciledBalance: 1512.08,  currency: "CAD", status: "overdue" },
  { id: "rec_004", accountId: "acc_cash",         accountName: "Petty cash",       bankBrand: "other", lastReconciledDate: null,         lastReconciledBalance: null,     currency: "CAD", status: "never" },
];

// =====================================================================
// Redesigned Overview (State B) mocks
// =====================================================================

/** Vela aggregate across every Accounting sub-page. */
export const MOCK_VELA_SUMMARY: VelaSummary = {
  totalCount: 7,
  breakdown: {
    toReview: 4,
    missingReceipts: 2,
    overdueReconcile: 1,
    pendingAccountant: 1,
  },
};

/** 6 months of cash flow for the Cash flow chart. */
export const MOCK_CASH_FLOW: CashFlowPoint[] = [
  { month: "May", moneyIn: 12800, moneyOut: 9900,  currency: "CAD" },
  { month: "Jun", moneyIn: 16100, moneyOut: 12400, currency: "CAD" },
  { month: "Jul", moneyIn: 14500, moneyOut: 14900, currency: "CAD" },
  { month: "Aug", moneyIn: 19800, moneyOut: 13700, currency: "CAD" },
  { month: "Sep", moneyIn: 18200, moneyOut: 11900, currency: "CAD" },
  { month: "Oct", moneyIn: 22850, moneyOut: 15300, currency: "CAD" },
];

/** Section status: one summary per operational sub-page. */
export const MOCK_SECTION_STATUS: SectionStatus = {
  transactions: { importedThisMonth: 142, needReview: 4 },
  receipts:     { matchedThisMonth: 28,  missing: 2 },
  reconcile:    { lastDoneDate: "2026-10-15", accountsOverdue: 1 },
  accountant:   { inviteeName: "Grace Nkiru", invitedDate: "2026-10-20", state: "pending" },
};